import type { LeaveStatus } from '@lms/shared-types'
import { Icon } from '../primitives/Icon'
import type { IconName } from '../primitives/Icon'

/** Counts across the viewer's WHOLE leave history (not the current page). */
export interface LeaveStatusCounts {
  pending: number
  approved: number
  rejected: number
}

interface LeaveMetricCardsProps {
  counts: LeaveStatusCounts
  /** The active status filter set — a card reads as "selected" when its
   *  status is present. */
  selectedStatuses: ReadonlySet<LeaveStatus>
  /** Toggle a status in/out of the filter (same handler the chip row uses). */
  onToggleStatus: (status: LeaveStatus) => void
}

/** A bg / fg / line accent triple mapped to semantic tokens so the tiles stay
 *  theme-aware (light + dark) instead of hard-coding hexes. */
interface Accent {
  bg: string
  fg: string
  line: string
}

const ACCENT_WARNING: Accent = {
  bg: 'var(--warning-bg)',
  fg: 'var(--warning-fg)',
  line: 'var(--warning-line)',
}
const ACCENT_SUCCESS: Accent = {
  bg: 'var(--success-bg)',
  fg: 'var(--success-fg)',
  line: 'var(--success-line)',
}
const ACCENT_DANGER: Accent = {
  bg: 'var(--danger-bg)',
  fg: 'var(--danger-fg)',
  line: 'var(--danger-line)',
}

interface Tile {
  status: LeaveStatus
  label: string
  icon: IconName
  accent: Accent
  count: number
}

/**
 * The three at-a-glance status tiles above the My Leaves history: Pending,
 * Approved, Rejected. Counts are computed over the viewer's whole history, so
 * they're stable regardless of the loaded page. Each tile is clickable and
 * toggles the matching status filter (mirrors the "on leave this week" stat
 * card on the Team page), with a "selected" ring when active.
 */
export function LeaveMetricCards({
  counts,
  selectedStatuses,
  onToggleStatus,
}: LeaveMetricCardsProps): JSX.Element {
  const tiles: Tile[] = [
    {
      status: 'pending',
      label: 'Pending',
      icon: 'schedule',
      accent: ACCENT_WARNING,
      count: counts.pending,
    },
    {
      status: 'approved',
      label: 'Approved',
      icon: 'check_circle',
      accent: ACCENT_SUCCESS,
      count: counts.approved,
    },
    {
      status: 'rejected',
      label: 'Rejected',
      icon: 'cancel',
      accent: ACCENT_DANGER,
      count: counts.rejected,
    },
  ]

  return (
    <div
      data-testid="leave-metric-cards"
      style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}
    >
      {tiles.map((tile) => {
        const active = selectedStatuses.has(tile.status)
        return (
          <button
            key={tile.status}
            type="button"
            onClick={() => onToggleStatus(tile.status)}
            aria-pressed={active}
            data-testid={`leave-metric-${tile.status}`}
            title={
              active
                ? `Filtering by ${tile.label} — click to clear`
                : `Filter by ${tile.label}`
            }
            style={{
              flex: '1 1 160px',
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
              padding: '14px 16px',
              border: `1px solid ${active ? tile.accent.fg : tile.accent.line}`,
              borderRadius: 12,
              background: tile.accent.bg,
              textAlign: 'left',
              boxSizing: 'border-box',
              cursor: 'pointer',
              transition: 'border-color 120ms ease, box-shadow 120ms ease',
              // Selected reads as a ring rather than a colour swap, so the
              // accent stays consistent between states.
              boxShadow: active ? `0 0 0 1px ${tile.accent.fg} inset` : 'none',
            }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Icon name={tile.icon} size={15} color={tile.accent.fg} />
              <span style={{ fontSize: 12, fontWeight: 600, color: tile.accent.fg }}>
                {tile.label}
              </span>
            </span>
            <span style={{ fontSize: 24, fontWeight: 700, color: 'var(--text)' }}>
              {tile.count}
            </span>
            <span style={{ fontSize: 11, color: tile.accent.fg, opacity: 0.85 }}>
              {active ? 'Filtering · click to clear' : 'Click to filter'}
            </span>
          </button>
        )
      })}
    </div>
  )
}
